import { useState, useEffect } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Play, Send, Loader2, Terminal } from "lucide-react";
import { clientHeaders } from "../lib/clientId";
import { normalizeMode } from "../lib/modes";

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export default function CodeEditor({ questionId, question, exampleInput, onCorrect, onSubmitSuccess, darkMode: darkModeProp, answerRevealed, timeRemaining, isDrillMode, drillId, classId, isAdminPreview, initialCode, onCodeChange, hideEditor = false, externalCode, extraPayload }) {
  const defaultCode = "# Write your Python code here\n";
  const [internalCode, setCode] = useState(initialCode || defaultCode);
  // Blocks mode drives the editor with generated Python instead of typed code
  const code = hideEditor ? (externalCode ?? "") : internalCode;
  // Use question prop for admin preview, otherwise use exampleInput
  const effectiveExampleInput = question?.example_input || exampleInput || "";
  const effectiveQuestionId = question?.question_id || questionId;
  const [customInput, setCustomInput] = useState(effectiveExampleInput);
  const [output, setOutput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Update code when initialCode prop changes OR when question changes
  // Prioritize initialCode (from cache) over defaultCode
  useEffect(() => {
    if (initialCode !== undefined && initialCode !== null) {
      setCode(initialCode);
    }
  }, [initialCode]);
  
  // Handle code changes and notify parent if callback provided
  const handleCodeChange = (newCode) => {
    setCode(newCode);
    if (onCodeChange) {
      onCodeChange(newCode);
    }
  };
  
  // Detect dark mode from document class if not passed as prop
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (darkModeProp !== undefined) return darkModeProp;
    return document.documentElement.classList.contains('dark');
  });
  
  // Update dark mode when prop changes or document class changes
  useEffect(() => {
    if (darkModeProp !== undefined) {
      setIsDarkMode(darkModeProp);
      return;
    }
    
    // Watch for dark mode changes via MutationObserver
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, [darkModeProp]);

  // Reset code and input when questionId changes (but respect initialCode from parent)
  useEffect(() => {
    // If initialCode is provided (e.g., from cache), use it instead of defaultCode
    if (initialCode !== undefined && initialCode !== null) {
      setCode(initialCode || defaultCode);
    } else {
      setCode(defaultCode);
    }
    setCustomInput(question?.example_input || exampleInput || "");
    setOutput("");
  }, [effectiveQuestionId, question?.example_input, exampleInput, initialCode]);

  const handleTest = async () => {
    setIsRunning(true);
    setOutput("");
    
    try {
      const response = await fetch(`${API}/code/test`, {
        method: "POST",
        headers: clientHeaders(),
        body: JSON.stringify({
          code,
          custom_input: customInput,
          mode: normalizeMode(question?.mode),
          workspace: extraPayload?.workspace || null,
        }),
      });

      const result = await response.json();
      
      if (result.profanity) {
        setOutput(`⚠️ ${result.error}${result.flagged_words?.length ? `\n\nFlagged: ${result.flagged_words.join(", ")}` : ""}`);
      } else if (result.success) {
        const outputText = result.output?.trim();
        setOutput(outputText ? outputText : "(No output - your code ran successfully but didn't print anything)");
      } else {
        setOutput(`Error: ${result.error || "Unknown error occurred"}`);
      }
    } catch (error) {
      setOutput(`Error: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setOutput("");

    try {
      // For admin preview mode, use a different endpoint that doesn't save progress
      const endpoint = isAdminPreview 
        ? `${API}/code/preview-submit`
        : `${API}/code/submit`;
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: clientHeaders(),
        credentials: "include",
        body: JSON.stringify({
          code,
          question_id: effectiveQuestionId,
          answer_revealed: answerRevealed || false,
          time_remaining: timeRemaining || 0,
          is_drill: isDrillMode || false,
          drill_id: drillId || null,
          class_id: classId || null,
          is_admin_preview: isAdminPreview || false,
          ...(extraPayload || {}),
        }),
      });

      const result = await response.json();
      
      if (result.profanity) {
        setOutput(`⚠️ ${result.message}${result.flagged_words?.length ? `\n\nFlagged: ${result.flagged_words.join(", ")}` : ""}`);
        return;
      }
      if (result.blocks_rule_error) {
        setOutput(`⚠️ ${result.message}`);
        return;
      }
      
      if (result.correct) {
        setOutput(`✓ Correct!\nPassed ${result.passed}/${result.total} test cases`);
        // Call appropriate callback
        if (isAdminPreview && onSubmitSuccess) {
          onSubmitSuccess(result);
        } else if (onCorrect) {
          onCorrect(result.points_earned || 0, result.already_answered || false, result);
        }
      } else {
        let outputText = `✗ Not quite right\nPassed ${result.passed}/${result.total} test cases`;
        
        // Show first failed test case hint
        if (result.results && result.results.length > 0) {
          const firstFailed = result.results.find(r => !r.passed);
          if (firstFailed && firstFailed.expected && firstFailed.actual) {
            outputText += `\n\nExpected: ${firstFailed.expected}\nGot: ${firstFailed.actual}`;
          } else if (firstFailed && firstFailed.error) {
            outputText += `\n\nError: ${firstFailed.error}`;
          }
        }
        
        setOutput(outputText);
      }
    } catch (error) {
      setOutput(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="code-editor-container">
      {/* Code Editor */}
      {!hideEditor && (
      <div className="code-editor-wrapper" data-testid="code-editor">
        <CodeMirror
          value={code}
          height="280px"
          extensions={[python()]}
          theme={isDarkMode ? oneDark : undefined}
          onChange={(value) => handleCodeChange(value)}
          className="text-sm"
        />
      </div>
      )}

      {/* Custom Input */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Terminal className="w-4 h-4" />
          Custom Input
        </label>
        <Textarea
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          placeholder="Enter your test input here..."
          className="font-mono text-sm resize-none h-24"
          data-testid="custom-input-field"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={handleTest}
          disabled={isRunning || isSubmitting}
          variant="outline"
          className="flex-1 gap-2 rounded-xl h-11"
          data-testid="test-code-btn"
        >
          {isRunning ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          Test with Custom Input
        </Button>
        
        <Button
          onClick={handleSubmit}
          disabled={isRunning || isSubmitting}
          className="flex-1 gap-2 rounded-xl h-11 bg-secondary hover:bg-secondary/90"
          data-testid="submit-code-btn"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          Submit for Grading
        </Button>
      </div>

      {/* Output */}
      {output && (
        <div 
          className={`io-block ${
            output.startsWith("✓") 
              ? "border-2 border-success bg-success/5" 
              : output.startsWith("✗") 
                ? "border-2 border-error bg-error/5" 
                : ""
          }`}
          data-testid="code-output"
        >
          <pre className="whitespace-pre-wrap break-words overflow-auto">{output}</pre>
        </div>
      )}
    </div>
  );
}
